import { call, put, select } from 'redux-saga/effects';
import {
  mediaDevicesUpdated,
  selectActiveMediaDevices,
  selectMediaDevicesByKind,
} from 'features/user-media/userMediaSlice';
import { enumerateDevices } from 'features/user-media/utils';
import { ActiveMediaDevices, EnumeratedMediaDevices } from 'features/user-media/types';
import {
  DeviceDifferences,
  findDeviceDifferences,
} from 'features/user-media/utils/findDeviceDifferences';
import { handleRemovedDevicesSaga } from 'features/streaming/sagas/handleRemovedDevicesSaga';
import { handleAddedDevicesSaga } from 'features/streaming/sagas/handleAddedDevicesSaga';
import { mediaDevicesChangeStarted } from 'features/user-media/actions';
import { logger } from 'utils/logger';

const countDevicesByKind = (devices: EnumeratedMediaDevices) => ({
  audioinput: devices.audioinput.length,
  videoinput: devices.videoinput.length,
  audiooutput: devices.audiooutput.length,
});

export function* handleDeviceAutoChangeSaga({
  payload: { devicechangeEvents },
}: ReturnType<typeof mediaDevicesChangeStarted>) {
  const currentMediaDevices: EnumeratedMediaDevices = yield select(selectMediaDevicesByKind);
  const activeMediaDevices: ActiveMediaDevices = yield select(selectActiveMediaDevices);
  const updatedDevices: EnumeratedMediaDevices = yield call(enumerateDevices);

  const { removedActiveDevices, addedDevices }: DeviceDifferences = yield call(
    findDeviceDifferences,
    updatedDevices,
    currentMediaDevices,
    activeMediaDevices
  );

  if (Object.keys(removedActiveDevices).length) {
    logger
      .remote({ capture: 'media-devices', action: true })
      .info('User physically removed device(s),', { ...removedActiveDevices, devicechangeEvents });
    yield call(handleRemovedDevicesSaga, updatedDevices, removedActiveDevices);
  } else if (Object.keys(addedDevices).length) {
    logger
      .remote({ capture: 'media-devices', action: true })
      .info('User added device(s),', { ...addedDevices, devicechangeEvents });
    yield call(handleAddedDevicesSaga, updatedDevices, addedDevices);
  } else {
    // A remove/re-add pair needs at least two events and nets to no difference, so log only bursts.
    if (devicechangeEvents > 1) {
      logger
        .remote({ capture: 'media-devices' })
        .info('Device change burst, no active device affected', {
          devicechangeEvents,
          devicesBefore: countDevicesByKind(currentMediaDevices),
          devicesAfter: countDevicesByKind(updatedDevices),
        });
    }
    yield put(
      mediaDevicesUpdated(updatedDevices, {
        updatePermissions: true,
      })
    );
  }
}
